import { DateFormat } from '@startuptools/ui/DateFormat';
import { Typography } from '@startuptools/ui/Typography';
import { Box } from '@startuptools/ui/Box';
import { Tooltip } from '@startuptools/ui/Tooltip';
import { IconButton } from '@startuptools/ui/IconButton';
import FilterListIcon from '@mui/icons-material/FilterList';
import { List } from '@startuptools/ui/List';
import { ListItem } from '@startuptools/ui/ListItem';
import { Checkbox } from '@startuptools/ui/Checkbox';
import { Button } from '@startuptools/ui/Button';
import { Trans, useLingui } from '@lingui/react';
import { SelfLoadingDocumentDialog } from '../../../../components/document-dialog/DocumentDialog';
import { GqlFileSignatoryStatus, type GqlBolagoFile } from '../../../../graphql/base-types.graphql';
import React from 'react';
import { DashboardTable, EmptyState } from './Table';
import { DocumentSignatureStatus } from '@startuptools/business-ui/document/DocumentSignatureStatus';
import { useNavigate } from '../../../../components/Navigate';
import { Link } from 'react-router-dom';

export const DocumentTable = ({
  documents,
  show,
  toggleDocumentStatus,
  toggleAll,
}: {
  documents: Pick<
    GqlBolagoFile,
    'id' | 'filename' | 'createdAt' | 'scriveStatus' | 'zignedAgreementStatus' | 'signatureMethod'
  >[];
  toggleDocumentStatus: (status: GqlFileSignatoryStatus, show: boolean) => void;
  toggleAll: () => void;
  show: Record<GqlFileSignatoryStatus, boolean>;
}) => {
  const { link } = useNavigate();
  const [hasFiltered, setHasFiltered] = React.useState(false);
  const { i18n } = useLingui();
  const [showDocumentDialog, setShowDocumentDialog] = React.useState<false | string>(false);

  const hasDocuments = documents.length > 0 || hasFiltered;

  return (
    <>
      <DashboardTable
        hasItems={hasDocuments}
        title={<Trans id="Documents" />}
        dateLabel={<Trans id="Created at" />}
        emptyState={
          <EmptyState
            header={
              <Trans id="You haven't created any documents yet. Get started by creating your first one - digital signing is included with all paid plans." />
            }
            cta={
              <Button size="sm" component={Link} to={link('/documents/company-documents')}>
                <Trans id="Create document" />
              </Button>
            }
          />
        }
        filter={
          <Tooltip
            variant="outlined"
            disableFocusListener
            title={
              <Box sx={{ p: 2 }}>
                <Typography
                  id="filter-status"
                  sx={{
                    textTransform: 'uppercase',
                    fontSize: 'xs',
                    letterSpacing: 'lg',
                    fontWeight: 'lg',
                    color: 'text.secondary',
                    mb: 2,
                  }}
                >
                  <Trans id="Filter by status" />
                </Typography>
                <div role="group" aria-labelledby="filter-status">
                  <List>
                    {Object.entries(show).map(([status, shown]) => {
                      return (
                        <ListItem variant="plain" sx={{ borderRadius: 'sm' }} key={status}>
                          <Checkbox
                            slotProps={{ label: { fontSize: 'sm' } }}
                            label={
                              <>
                                {status === GqlFileSignatoryStatus.Pending ? (
                                  <Trans id="Ready for signing" />
                                ) : status === GqlFileSignatoryStatus.Ongoing ? (
                                  <Trans id="Ongoing" />
                                ) : (
                                  <Trans id="Done" />
                                )}
                              </>
                            }
                            checked={shown}
                            onChange={event => {
                              setHasFiltered(true);
                              toggleDocumentStatus(status as GqlFileSignatoryStatus, event.target.checked);
                            }}
                            sx={{ color: 'inherit' }}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </div>
                <Button
                  variant="outlined"
                  color="neutral"
                  size="sm"
                  onClick={() => {
                    setHasFiltered(true);
                    toggleAll();
                  }}
                  sx={{ px: 1.5, mt: 1 }}
                >
                  {Object.values(show).some(Boolean) ? <Trans id="Hide all" /> : <Trans id="show all" />}
                </Button>
              </Box>
            }
          >
            <IconButton size="sm" variant="outlined" color="neutral">
              <FilterListIcon />
            </IconButton>
          </Tooltip>
        }
        rows={documents.map(c => (
          <tr key={c.id} onClick={() => setShowDocumentDialog(c.id)}>
            <td>{c.filename}</td>
            <td>
              <DateFormat locale={i18n.locale}>{c.createdAt}</DateFormat>
            </td>
            <td>
              <DocumentSignatureStatus
                scriveStatus={c.scriveStatus}
                zignedAgreementStatus={c.zignedAgreementStatus}
                signatureMethod={c.signatureMethod}
              />
            </td>
          </tr>
        ))}
      />
      {showDocumentDialog && (
        <SelfLoadingDocumentDialog
          documentId={showDocumentDialog}
          onClose={() => {
            setShowDocumentDialog(false);
          }}
        />
      )}
    </>
  );
};
